/* Calls and lives on the feed — a row of tall cards, like a stories row.
 *
 * WHY ON THE FEED: a member does not go looking for a page called Calls. A
 * call happens at a moment, so the reminder has to be where they already
 * look. But not as a wall of cards above the posts: one row under the
 * composer, scrolled sideways, that takes the same space however busy the
 * week is.
 *
 * FIRST, the way to everything: a "Coming up" card that opens the page
 * with the whole list. Then what is on now, marked LIVE, and then the next
 * few, each with its day and time. Tapping a card goes where that call is
 * — the live, or the room once it has opened — and otherwise to the list.
 *
 * Nothing arranged, nothing rendered.
 */
import { Link } from 'react-router-dom'
import { CalendarDays, Mic, Radio, Video } from 'lucide-react'
import { useFeatures, useMyCalls } from '@/api/queries'
import { cn } from '@/lib/utils'
import type { CallKind, ScheduledCall } from '@/types/api'

export const KIND_ICON: Record<CallKind, typeof Mic> = {
  audio: Mic,
  video: Video,
  live: Radio,
}

export const KIND_NAME: Record<CallKind, string> = {
  audio: 'Meeting',
  video: 'Video meeting',
  live: 'Live',
}

/** A scheduled call opens this long before its time (the server's rule). */
export const OPENS_BEFORE_MS = 15 * 60 * 1000

/** On now, or started the moment it was made. */
export function isOn(call: ScheduledCall): boolean {
  return call.status === 'live' || call.is_immediate || !call.starts_at
}

/** Can be entered: on, or within its quarter of an hour. */
export function isOpen(call: ScheduledCall): boolean {
  if (isOn(call)) return true
  return new Date(call.starts_at!).getTime() - Date.now() <= OPENS_BEFORE_MS
}

/** Where tapping it goes. A live's page shows "starting soon" before it
 *  begins; a room that has not opened would only refuse. */
export function callPath(call: ScheduledCall): string {
  if (call.kind === 'live') return `/calls/${call.id}/live`
  if (isOpen(call)) return `/calls/${call.id}/room`
  return '/coming-up'
}

/** Whether the caller's church lets them into calls of this kind. Watching a
 *  live is always open; meetings follow the church's voice and video. */
export function useJoinable(): (call: ScheduledCall) => boolean {
  const f = useFeatures()
  return (call) =>
    call.kind === 'live' ||
    (call.kind === 'audio' ? f.audio_conf_calls : f.video_conf_calls)
}

export function dayAndTime(iso: string): string {
  const at = new Date(iso)
  return `${at.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric' })} · ${at.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}`
}

const TINT: Record<CallKind, string> = {
  live: 'from-[#e0314f] to-[#f08a3c]',
  audio: 'from-[#1d63d8] to-[#3aa0e8]',
  video: 'from-[#6d3fd1] to-[#b05ad8]',
}

export function CallsRow({ enabled = true }: { enabled?: boolean }) {
  const calls = useMyCalls(enabled)
  const joinable = useJoinable()
  const rows = (calls.data ?? []).filter(joinable)
  if (!rows.length) return null

  // What is on first, then soonest.
  const ordered = [...rows.filter(isOn), ...rows.filter((c) => !isOn(c))]

  return (
    <div className="-mx-s1 flex snap-x gap-s2 overflow-x-auto px-s1 pb-1 [scrollbar-width:none]">
      <Link
        to="/coming-up"
        className="flex h-[200px] w-[124px] shrink-0 snap-start flex-col overflow-hidden rounded-lg bg-surface shadow-1 transition-transform hover:-translate-y-0.5"
      >
        <span className="grid flex-1 place-items-center bg-brand-softer">
          <CalendarDays className="h-10 w-10 text-brand" aria-hidden />
        </span>
        <span className="relative px-s2 pb-s3 pt-s5 text-center">
          <span className="absolute -top-5 left-1/2 grid h-10 w-10 -translate-x-1/2 place-items-center rounded-pill border-4 border-surface bg-brand text-sm font-extrabold text-ink-on-brand">
            {rows.length}
          </span>
          <span className="block text-sm font-bold">Coming up</span>
        </span>
      </Link>
      {ordered.slice(0, 8).map((call) => (
        <CallTile key={call.id} call={call} />
      ))}
    </div>
  )
}

function CallTile({ call }: { call: ScheduledCall }) {
  const Icon = KIND_ICON[call.kind]
  const on = isOn(call)
  const host = call.moderator_name ?? call.created_by_name ?? ''
  const initials = host
    .split(' ')
    .map((w) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase()

  return (
    <Link
      to={callPath(call)}
      title={call.description ?? call.title}
      className={cn(
        'relative flex h-[200px] w-[124px] shrink-0 snap-start flex-col justify-between overflow-hidden rounded-lg bg-gradient-to-br p-s2 text-white shadow-1 transition-transform hover:-translate-y-0.5',
        TINT[call.kind],
      )}
    >
      <Icon
        className="pointer-events-none absolute -right-3 top-1/3 h-24 w-24 opacity-15"
        aria-hidden
      />
      <span className="flex items-start justify-between">
        <span
          className={cn(
            'grid h-9 w-9 place-items-center rounded-pill bg-white/25 text-xs font-extrabold ring-[3px]',
            on ? 'ring-white' : 'ring-white/60',
          )}
        >
          {initials || <Icon className="h-4 w-4" aria-hidden />}
        </span>
        {on ? (
          <span className="flex items-center gap-1 rounded-sm bg-danger px-1.5 py-0.5 text-[10px] font-extrabold uppercase tracking-wide shadow-1">
            <span className="h-1.5 w-1.5 animate-pulse rounded-pill bg-white" />
            Live
          </span>
        ) : null}
      </span>
      <span className="relative">
        <span className="line-clamp-3 text-sm font-bold leading-snug drop-shadow">
          {call.title}
        </span>
        <span className="mt-1 block text-[11px] font-semibold opacity-90">
          {on ? (call.kind === 'live' ? 'Watch now' : 'Join now') : dayAndTime(call.starts_at!)}
        </span>
      </span>
    </Link>
  )
}
